import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  ListItemIcon,
  ListItemText,
  FormHelperText,
} from '@material-ui/core'
import InfoIcon from 'components/material-symbol/icons/Info'
import useStyles from './styles'
import { useStyles as useEditFormStyles } from 'pages/pga-coach/bookings/events/edit/edit-event-form/styles'
import { Controller, useFormContext } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import StartTimeIncrementPreviewDialog from 'components/start-time-increment-preview-dialog'
import StartTimeIncrementField from './start-time-increment-field'
import { useAuth } from 'lib/auth'
import { FRISCO_COACHING_CENTER_SLUG } from 'utils/constants'
import Disclaimer from 'components/disclaimer'
import ButtonCta from 'components/atoms/button-cta'
import DebouncedMarkdownRichTextEditor from 'lib/rte-utils/debounced-markdown-rich-text-editor'
import {
  optionalLabel,
  requiredLabel,
  REQUIRED_INPUT_PROPS,
  RequiredMark,
} from './required-field'

const BookingTypeDetails = ({
  coachFacilities,
  coachAvailabilities,
  coachingCenters = [],
  durationInMinutesOptions,
  categories,
  handleCategoryChange,
  startTimeIncrementOptions,
  isEditFlow = false,
  isVirtualCoaching = false,
}) => {
  const { errors, control, register, watch, setValue } = useFormContext()
  const classes = useStyles()
  const editClasses = useEditFormStyles()
  const fieldClass = isEditFlow ? editClasses.fieldInput : classes.formField
  const coachingCenterId = watch('coachingCenter') || ''
  const { user } = useAuth()
  const { bookingTypeId } = useParams()
  const [showAvailabilityWarning, setShowAvailabilityWarning] = useState(false)
  const friscoCoachingCenter = coachingCenters?.find(
    cc => cc.slug === FRISCO_COACHING_CENTER_SLUG,
  )
  const showCoachingCenterDropdown =
    (user.isAcademyStaff || user.isAcademyOwner) && friscoCoachingCenter

  const descriptionEditor = (
    <DebouncedMarkdownRichTextEditor
      name="description"
      label="Add a description"
      controls={['title', 'bold', 'italic', 'bulletList', 'link']}
      markdownToDraftOptions={{ preserveNewlines: true }}
      draftToMarkdownOptions={{ preserveNewlines: true }}
    />
  )

  const coachingCentersWithNone = [
    { id: '', name: 'None', facilities: [] },
    { ...friscoCoachingCenter },
  ] // selecting none clears the facility filter

  const filteredCoachFacilities =
    showCoachingCenterDropdown && coachingCenterId
      ? friscoCoachingCenter.facilities
      : coachFacilities

  const prevCoachingCenterIdRef = useRef(coachingCenterId)

  useEffect(() => {
    // Virtual coaching has no location, so there is nothing to auto-select.
    if (isVirtualCoaching || !showCoachingCenterDropdown || !coachingCenterId) {
      prevCoachingCenterIdRef.current = coachingCenterId
      return
    }

    if (prevCoachingCenterIdRef.current !== coachingCenterId) {
      const nextLocationId = filteredCoachFacilities[0]?.id
      if (nextLocationId) {
        setValue('location', nextLocationId, {
          shouldValidate: true,
          shouldDirty: true,
        })
      }
      prevCoachingCenterIdRef.current = coachingCenterId
    }
  }, [
    isVirtualCoaching,
    showCoachingCenterDropdown,
    coachingCenterId,
    filteredCoachFacilities,
    setValue,
  ])

  const editFieldGroup = (heading, helper, field) => (
    <Box className={editClasses.fieldGroup}>
      {heading ? (
        <Typography className={editClasses.sectionHeading}>
          {heading}
        </Typography>
      ) : null}
      {helper ? (
        <Typography className={editClasses.fieldHelper}>{helper}</Typography>
      ) : null}
      <Box
        className={helper ? undefined : editClasses.fieldControlAfterHeading}
      >
        {field}
      </Box>
    </Box>
  )

  // `coachingCenterOnly` renders just the Coaching Center select. Virtual
  // coaching uses it because it can have an academy but not a location.
  const facilitySelectWithCoachingCenters = ({
    coachingCenterOnly = false,
  } = {}) => {
    if (isEditFlow) {
      return (
        <>
          {editFieldGroup(
            optionalLabel('Coaching Center'),
            'Select the coaching center where this lesson type will be associated.',
            <FormControl
              variant="outlined"
              className={editClasses.fieldInput}
              disabled={bookingTypeId}
              title={
                bookingTypeId
                  ? 'You cannot change the coaching center for an existing booking type'
                  : ''
              }
              fullWidth
            >
              <InputLabel id="coaching-center-label">
                Coaching Center
              </InputLabel>
              <Controller
                render={({ onChange, value }) => (
                  <Select
                    labelId="coaching-center-label"
                    label="Coaching Center"
                    value={value ?? ''}
                    data-cy="coachingCenter"
                    onChange={onChange}
                  >
                    {coachingCentersWithNone?.map((opt, idx) => (
                      <MenuItem
                        key={opt.id}
                        value={opt.id}
                        data-cy={`coachingCenter-${idx}`}
                      >
                        {opt.name}
                      </MenuItem>
                    ))}
                  </Select>
                )}
                name="coachingCenter"
                control={control}
              />
            </FormControl>,
          )}
          {!coachingCenterOnly &&
            editFieldGroup(
              'Location',
              'Select the facility where this booking type will be associated.',
              <FormControl
                error={!!errors.location || !filteredCoachFacilities.length}
                variant="outlined"
                className={editClasses.fieldInput}
                fullWidth
              >
                <InputLabel id="location-label">
                  {requiredLabel('Location')}
                </InputLabel>
                <Controller
                  render={({ onChange, value }) => (
                    <Select
                      labelId="location-label"
                      label={requiredLabel('Location')}
                      value={value ?? ''}
                      data-cy="location"
                      onChange={onChange}
                    >
                      {filteredCoachFacilities?.map(opt => (
                        <MenuItem key={opt.id} value={opt.id}>
                          {opt.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                  name="location"
                  control={control}
                />
                {errors.location ? (
                  <FormHelperText>
                    A location is required to create a booking type
                  </FormHelperText>
                ) : null}
              </FormControl>,
            )}
        </>
      )
    }

    return (
      <>
        <Typography variant="body2" gutterBottom>
          Select the Coaching Center where this lesson type will be associated.
        </Typography>
        <FormControl
          variant="outlined"
          className={fieldClass}
          disabled={bookingTypeId}
          title={
            bookingTypeId
              ? 'You cannot change the coaching center for an existing booking type'
              : ''
          }
          fullWidth={isEditFlow}
        >
          <InputLabel id="coaching-center-label">Coaching Center</InputLabel>
          <Controller
            render={({ onChange, value }) => (
              <Select
                labelId="coaching-center-label"
                label="Coaching Center"
                value={value ?? ''}
                data-cy="coachingCenter"
                onChange={onChange}
              >
                {coachingCentersWithNone?.map((opt, idx) => (
                  <MenuItem
                    key={opt.id}
                    value={opt.id}
                    data-cy={`coachingCenter-${idx}`}
                  >
                    {opt.name}
                  </MenuItem>
                ))}
              </Select>
            )}
            name="coachingCenter"
            control={control}
          />
        </FormControl>
        {coachingCenterOnly ? null : (
          <>
            {isEditFlow ? (
              <Typography className={editClasses.fieldHelper}>
                Select the facility where this booking type will be associated.
              </Typography>
            ) : (
              <Typography variant="body2" gutterBottom>
                Select the facility where this booking type will be associated.
              </Typography>
            )}
            <FormControl
              error={!!errors.location || !filteredCoachFacilities.length}
              variant="outlined"
              className={fieldClass}
              fullWidth={isEditFlow}
            >
              <InputLabel id="location-label">
                {requiredLabel('Location')}
              </InputLabel>
              <Controller
                render={({ onChange, value }) => (
                  <Select
                    labelId="location-label"
                    label={requiredLabel('Location')}
                    value={value ?? ''}
                    data-cy="location"
                    onChange={onChange}
                  >
                    {filteredCoachFacilities?.map(opt => (
                      <MenuItem key={opt.id} value={opt.id}>
                        {opt.name}
                      </MenuItem>
                    ))}
                  </Select>
                )}
                name="location"
                control={control}
              />
              {errors.location ? (
                <FormHelperText>
                  A location is required to create a booking type
                </FormHelperText>
              ) : null}
            </FormControl>
          </>
        )}
      </>
    )
  }
  const facilitySelect = () => {
    const handleLocationChange = e => {
      const selectedFacility = coachAvailabilities.find(
        item => item.coachFacility.id === e.target.value,
      )
      setShowAvailabilityWarning(!selectedFacility?.timeSlots?.length)
    }

    if (isEditFlow) {
      return editFieldGroup(
        'Location',
        'Select the location where this booking type will be associated.',
        <FormControl
          error={!!errors.location || !coachFacilities.length}
          variant="outlined"
          className={editClasses.fieldInput}
          fullWidth
        >
          <InputLabel id="location-label">
            {requiredLabel('Location')}
          </InputLabel>
          <Controller
            render={({ onChange, value }) => (
              <Select
                labelId="location-label"
                label={requiredLabel('Location')}
                value={value || ''}
                data-cy="location"
                onChange={e => {
                  onChange(e)
                  handleLocationChange(e)
                }}
              >
                {coachFacilities?.map(opt => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.name}
                  </MenuItem>
                ))}
              </Select>
            )}
            name="location"
            control={control}
          />
          {errors.location ? (
            <FormHelperText>
              {errors.location?.message || 'A location is required'}
            </FormHelperText>
          ) : null}
        </FormControl>,
      )
    }

    return (
      <>
        {isEditFlow ? (
          <Typography className={editClasses.fieldHelper}>
            Select the location where this booking type will be associated.
          </Typography>
        ) : (
          <Typography variant="body2" gutterBottom>
            Select the location where this booking type will be associated.
          </Typography>
        )}
        <FormControl
          error={!!errors.location || !coachFacilities.length}
          variant="outlined"
          className={fieldClass}
          fullWidth={isEditFlow}
        >
          <InputLabel id="location-label">
            {requiredLabel('Location')}
          </InputLabel>
          <Controller
            render={({ onChange, value }) => (
              <Select
                labelId="location-label"
                label={requiredLabel('Location')}
                value={value || ''}
                data-cy="location"
                onChange={e => {
                  onChange(e)
                  handleLocationChange(e)
                }}
              >
                {coachFacilities?.map(opt => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.name}
                  </MenuItem>
                ))}
              </Select>
            )}
            name="location"
            control={control}
          />
          {errors.location ? (
            <FormHelperText>
              {errors.location?.message || 'A location is required'}
            </FormHelperText>
          ) : null}
        </FormControl>
      </>
    )
  }

  return (
    <Box>
      <Box mt={isEditFlow ? 0 : 3}>
        {isEditFlow ? (
          <>
            {!isVirtualCoaching &&
              editFieldGroup(
                'Category',
                'Select from the categories below to indicate the type of booking.',
                <FormControl
                  error={!!errors.category}
                  variant="outlined"
                  className={editClasses.fieldInput}
                  fullWidth
                >
                  <InputLabel id="category-label">
                    {requiredLabel('Category')}
                  </InputLabel>
                  <Controller
                    render={({ onChange, value }) => (
                      <Select
                        labelId="category-label"
                        data-cy="category"
                        label={requiredLabel('Category')}
                        onChange={e => {
                          onChange(e)
                          handleCategoryChange(e)
                        }}
                        value={value || ''}
                      >
                        {categories.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                    name="category"
                    control={control}
                  />
                  {errors.category ? (
                    <FormHelperText>
                      A Category is required to create a booking type
                    </FormHelperText>
                  ) : null}
                </FormControl>,
              )}
            <Box className={editClasses.fieldGroup}>
              <Controller
                name="title"
                control={control}
                render={({ onChange, onBlur, value }) => (
                  <TextField
                    className={editClasses.fieldInput}
                    data-cy="title"
                    label={
                      isVirtualCoaching
                        ? 'Offering name *'
                        : 'Booking type name *'
                    }
                    value={value || ''}
                    onChange={onChange}
                    onBlur={onBlur}
                    error={!!errors.title}
                    helperText={errors.title && 'A title is required'}
                    inputProps={REQUIRED_INPUT_PROPS}
                    variant="outlined"
                    fullWidth
                  />
                )}
              />
            </Box>
          </>
        ) : (
          <>
            <Typography variant="subtitle1" gutterBottom>
              Category
            </Typography>
            <Typography variant="body2" gutterBottom>
              Select from the categories below to indicate the type of booking.
            </Typography>
            <FormControl
              error={!!errors.category}
              className={classes.formField}
            >
              <InputLabel id="category">
                {requiredLabel('Select category')}
              </InputLabel>
              <Controller
                render={({ onChange, value }) => (
                  <Select
                    labelId="category"
                    data-cy="category"
                    label={requiredLabel('Select category')}
                    onChange={e => {
                      onChange(e)
                      handleCategoryChange(e)
                    }}
                    value={value || ''}
                    classes={{
                      root: classes.menuItem,
                    }}
                  >
                    {categories.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>
                        <ListItemIcon className={classes.icon}>
                          {opt.icon}
                        </ListItemIcon>
                        <ListItemText>{opt.label}</ListItemText>
                      </MenuItem>
                    ))}
                  </Select>
                )}
                name="category"
                control={control}
              />
              {errors.category ? (
                <FormHelperText>
                  A Category is required to create a booking type
                </FormHelperText>
              ) : null}
            </FormControl>
            <Typography variant="subtitle1" gutterBottom>
              Name
              <RequiredMark />
            </Typography>
            <Typography variant="body2" gutterBottom>
              Enter a name for this booking type. Feel free to edit the
              suggested name based on the category you&apos;ve selected.
            </Typography>
            <TextField
              className={classes.formField}
              name="title"
              data-cy="title"
              inputRef={register}
              error={!!errors.title}
              helperText={errors.title && 'A title is required'}
              inputProps={REQUIRED_INPUT_PROPS}
            />
          </>
        )}
        {isVirtualCoaching ? (
          // Virtual coaching can't have a location, but may have an academy.
          showCoachingCenterDropdown ? (
            isEditFlow ? (
              facilitySelectWithCoachingCenters({ coachingCenterOnly: true })
            ) : (
              <>
                <Typography variant="subtitle1" gutterBottom>
                  Coaching Center
                </Typography>
                {facilitySelectWithCoachingCenters({
                  coachingCenterOnly: true,
                })}
              </>
            )
          ) : null
        ) : isEditFlow ? (
          showCoachingCenterDropdown ? (
            facilitySelectWithCoachingCenters()
          ) : (
            facilitySelect()
          )
        ) : (
          <>
            <Typography variant="subtitle1" gutterBottom>
              Location
            </Typography>
            {showCoachingCenterDropdown
              ? facilitySelectWithCoachingCenters()
              : facilitySelect()}
          </>
        )}

        {!isVirtualCoaching && showAvailabilityWarning && (
          <Disclaimer
            icon={<InfoIcon />}
            description={
              <>
                <Typography variant="body2">
                  To make this booking type available for your students, make
                  sure to set the facility's availability.
                </Typography>
                <ButtonCta
                  variant="text"
                  arrow
                  href={'/pga-coach/settings/availability'}
                  className={classes.ctaButton}
                >
                  Add Availability
                </ButtonCta>
              </>
            }
          />
        )}

        {isEditFlow ? (
          editFieldGroup(
            optionalLabel('Description'),
            isVirtualCoaching
              ? 'Describe what is included, what kind of golfer it is best for, and how to film a good swing video. Students see this before they buy.'
              : 'Provide a description for this booking type. Include information that would be helpful for your students to know when they book a session.',
            <Box className={editClasses.editorRoot}>{descriptionEditor}</Box>,
          )
        ) : (
          <>
            <Typography variant="subtitle1" gutterBottom>
              {optionalLabel('Description')}
            </Typography>
            <Typography variant="body2" gutterBottom>
              Provide a description for this booking type. Include information
              that would be helpful for your students to know when they book a
              session.
            </Typography>
            <Box className={classes.editorRoot}>{descriptionEditor}</Box>
          </>
        )}
        {isVirtualCoaching ? null : isEditFlow ? (
          editFieldGroup(
            'Duration',
            'Select a time duration to indicate how long each session under this booking type will last.',
            <FormControl
              error={!!errors.durationInMinutes}
              variant="outlined"
              className={editClasses.fieldInput}
              fullWidth
            >
              <InputLabel id="durationInMinutes">
                {requiredLabel('Duration')}
              </InputLabel>
              <Controller
                render={({ onChange, value }) => (
                  <Select
                    labelId="durationInMinutes"
                    label={requiredLabel('Duration')}
                    data-cy="duration"
                    value={value ?? ''}
                    onChange={onChange}
                  >
                    {durationInMinutesOptions.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
                name="durationInMinutes"
                control={control}
              />
              {errors.durationInMinutes ? (
                <FormHelperText>
                  A duration is required to create a booking type
                </FormHelperText>
              ) : null}
            </FormControl>,
          )
        ) : (
          <>
            <Typography variant="subtitle1" gutterBottom>
              Duration
            </Typography>
            <Typography variant="body2" gutterBottom>
              Select a time duration to indicate how long each session under
              this booking type will last.
            </Typography>
            <FormControl
              error={!!errors.durationInMinutes}
              className={classes.formField}
            >
              <InputLabel id="durationInMinutes">
                {requiredLabel('Select time duration')}
              </InputLabel>
              <Controller
                render={({ onChange, value }) => (
                  <Select
                    labelId="durationInMinutes"
                    label={requiredLabel('Select time duration')}
                    data-cy="duration"
                    value={value ?? ''}
                    onChange={onChange}
                  >
                    {durationInMinutesOptions.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
                name="durationInMinutes"
                control={control}
              />
              {errors.durationInMinutes ? (
                <FormHelperText>
                  A duration is required to create a booking type
                </FormHelperText>
              ) : null}
            </FormControl>
          </>
        )}
        {isVirtualCoaching ? null : isEditFlow ? (
          <Box className={editClasses.fieldGroup}>
            <Box className={classes.availabilitySection}>
              <Box className={classes.incrementHeader}>
                <Typography className={editClasses.sectionHeading}>
                  Start Time Increment
                  <RequiredMark />
                </Typography>
                <StartTimeIncrementPreviewDialog
                  linkTrigger
                  startTimeIncrementOptions={startTimeIncrementOptions}
                />
              </Box>
              <Typography className={editClasses.fieldHelper}>
                Set the frequency of available start times shown in the booking
                calendar.
              </Typography>
              <StartTimeIncrementField
                control={control}
                errors={errors}
                startTimeIncrementOptions={startTimeIncrementOptions}
              />
            </Box>
          </Box>
        ) : (
          <Box className={classes.availabilitySection}>
            <Box className={classes.incrementHeader}>
              <Typography variant="subtitle1">
                Start Time Increment
                <RequiredMark />
              </Typography>
              <StartTimeIncrementPreviewDialog
                linkTrigger
                startTimeIncrementOptions={startTimeIncrementOptions}
              />
            </Box>
            <Typography variant="body2" gutterBottom>
              Set the frequency of available start times shown in the booking
              calendar.
            </Typography>
            <StartTimeIncrementField
              control={control}
              errors={errors}
              startTimeIncrementOptions={startTimeIncrementOptions}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default BookingTypeDetails
